import React from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  MAX_TARGET_PERCENT,
  MIN_TARGET_PERCENT,
  formatRupees,
  getTargetCost,
  validateTargetPercent,
} from "../../utils/utilisation";
import { useUtilisationColors } from "./useUtilisationColors";

const NOTES = {
  create: "Team is eligible for incentive only if the project is completed within this target.",
  edit: "Budget and Target % can be changed anytime. Every change is saved in the change log (old value, new value, who, when).",
};

/**
 * "Target %" input for the Create / Edit Project forms (Fixed Cost projects only).
 * Shows the resulting target cost so the number is concrete: Budget x Target %.
 *
 * Controlled: pass `value` (string or number) and `onChange(event)`.
 * It only displays a message once there is something to say - validation errors
 * are shown when `showError` is set (e.g. after the field was touched).
 *
 * @param {"create"|"edit"} props.mode  - picks the helper note
 * @param {number|string}   props.budget - the project's Total Budget, for the target cost line
 */
const TargetPercentField = ({
  value,
  onChange,
  onBlur,
  budget,
  mode = "create",
  disabled = false,
  showError = false,
  id = "target_percent",
  inputRef,
  name = "target_percent",
}) => {
  const colors = useUtilisationColors();
  const error = showError ? validateTargetPercent(value) : null;
  const isValid = validateTargetPercent(value) === null;
  const hasBudget = Number(budget) > 0;
  const helperId = `${id}-helper`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <label htmlFor={id} className="form-label mb-0">
        Target % <span className="text-rgbr-red"> *</span>
      </label>
      <TextField
        id={id}
        name={name}
        type="number"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        error={Boolean(error)}
        inputRef={inputRef}
        inputProps={{ min: MIN_TARGET_PERCENT, max: MAX_TARGET_PERCENT, step: 1, "aria-describedby": helperId }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        fullWidth
      />

      <Box id={helperId} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {error ? (
          <Typography role="alert" sx={{ fontSize: 12, color: colors.red }}>
            {error}
          </Typography>
        ) : isValid && hasBudget ? (
          <Typography data-testid="target-cost" sx={{ fontSize: 12, fontWeight: 600, color: colors.tealDeep }}>
            Target cost: {formatRupees(getTargetCost(budget, value))} ({Number(value)}% of {formatRupees(budget)})
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: colors.ink3 }}>Enter the budget to see the target cost.</Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", color: colors.ink2 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, lineHeight: 1.45 }}>{NOTES[mode] || NOTES.create}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TargetPercentField;
